import React, { useLayoutEffect, useRef, useState } from 'react';
import { useClickOutside } from '../../hooks/useClickOutside';

interface SortDropdownProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSortBy: React.Dispatch<React.SetStateAction<string>>;
  setSortOrder: React.Dispatch<React.SetStateAction<string>>;
  sortBy: string;
  sortOrder: string;
}

const SortDropdown: React.FC<SortDropdownProps> = ({ isOpen, setIsOpen, setSortBy, setSortOrder, sortBy, sortOrder }) => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  // Positioned "fixed" (computed from the trigger icon's own position) instead
  // of "absolute" — the page's scroll container has overflow-x-hidden, which
  // clips an absolutely-positioned dropdown that overflows it horizontally.
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const DROPDOWN_WIDTH = 224; // w-56
  const EDGE_MARGIN = 16;

  // Close dropdown when clicking outside
  useClickOutside(dropdownRef, () => setIsOpen(false), true, "pointerdown");

  useLayoutEffect(() => {
    if (!isOpen) return;
    const trigger = dropdownRef.current?.parentElement;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    // Anchor the dropdown's right edge under the icon, but clamp so it never
    // runs past the left (or right) edge of the viewport — the icon isn't
    // always near the screen's right edge on narrow layouts.
    const idealLeft = rect.right - DROPDOWN_WIDTH;
    const maxLeft = window.innerWidth - DROPDOWN_WIDTH - EDGE_MARGIN;
    const left = Math.min(Math.max(idealLeft, EDGE_MARGIN), Math.max(maxLeft, EDGE_MARGIN));
    setPosition({
      top: rect.bottom + 8,
      left,
    });
  }, [isOpen]);

  if (!isOpen) return null;

  // Function to handle sorting option click
  const handleSortChange = (sortOption: string) => {
    // Posted Date has no order (empty sortBy + empty sortOrder in the payload).
    if (sortOption === '') {
      setSortBy('');
      setSortOrder('');
    } else if (sortBy === sortOption) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); // Toggle order if the same option is clicked
    } else {
      setSortBy(sortOption);
      setSortOrder('asc'); // Default to ascending when a new option is selected
    }
    setIsOpen(false); // Close the dropdown once a sort option is applied
  };

  return (
    <div ref={dropdownRef}>
      <div
        id="dropdown-3"
        style={
          position
            ? { top: position.top, left: position.left }
            : { visibility: "hidden" }
        }
        className="z-50 bg-white divide-y divide-gray-100 rounded-lg shadow-lg border border-gray-200 w-56 max-w-[calc(100vw-2rem)] fixed origin-top-right"
      >
        <ul className="px-4 text-sm text-gray-700" aria-labelledby="dropdownDefaultButton">
          {/* 🔹 Sort By Title with Clickable Sorting Icon */}
          <p className="py-4 border-b border-gray-200 font-semibold text-gray-700 flex justify-between items-center">
            Sort By
            {sortBy !== '' && (
              <button onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}>
                <img
                  src={`/images/sort-${sortOrder}.svg`}
                  alt={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                  className="w-5 h-5 cursor-pointer"
                />
              </button>
            )}
          </p>

          {/* Sort options */}
          {[
            { key: '', icon: 'st-calendar', label: 'Posted Date' },
            // { key: 'priority', icon: 'st-priority', label: 'Priority' },
            { key: 'departure', icon: 'st-arrow-slant', label: 'Departure Time' },
            { key: 'route', icon: 'st-route', label: 'Route' },
            { key: 'flightNumber', icon: 'st-flight-slim', label: 'Flight Number' },
            { key: 'duration', icon: 'st-time', label: 'Duration' },
            { key: 'airline', icon: 'st-flight', label: 'Airline' },
            { key: 'arrival', icon: 'st-arrow-slant', label: 'Arrival', rotation: 'rotate-90' }
          ].map(({ key, icon, label, rotation }) => (
            <li
              key={key}
              onClick={() => handleSortChange(key)}
              className="flex items-center gap-2 py-2 px-3 cursor-pointer"
            >
              <i className={`${icon} text-sm ${rotation || ''} ${sortBy === key ? 'text-[#FFBC00]' : 'text-lightgray-150'}`}></i>
              <p className={`text-sm ${sortBy === key ? 'text-[#FFBC00] font-semibold' : 'text-lightgray-200'}`}>
                {label}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default SortDropdown;
